import React, { ReactNode } from 'react';

import { FormattedMessage, FormattedNumber, IntlShape } from 'react-intl';
import { DefaultTheme } from 'styled-components';

import {
    domFormatter,
    dateTimeFormatter,
    hockeyFormatter,
    statFormatter,
    stringFormatter
} from '@nhl/formatter-utilities';
import { BaseStyleEnums, ReactTableEnums, ReactTableTypes, SortableTableHeader } from '@nhl/nhl-breakaway-ui';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';

import { commonMessages, reportSpecificMessages } from '~/utility/commonMessages';

import { PlayerNameCell, HeaderCell, GameCell } from '../../tableCells';

import { getMinColumnSize } from './columnSizing';
import { Column, ReportMeasure, StatsRecord } from './types';

const LEFT_ALIGNED_COLUMN = {
    className: 'left-aligned',
    headerClassName: 'left-aligned',
};

export const columnHelper = createColumnHelper<StatsRecord>();

/* This switch statement is to cover custom cell rendering.
   A few cells needed custom cells so the switch was implemented to easily add/remove cases.
   If a column in one report needs to be different than the same column in a different report modify the
   function to include report as a param and modify dataReformatter to include report from appliedConfig.
*/
const cellFormatter = (
    intl: IntlShape,
    reportMeasure: ReportMeasure,
    reportCategory: string,
    reportName: string,
    theme: DefaultTheme,
): AccessorKeyColumnDef<StatsRecord> => {
    const translateIfExist = (label) => label && intl.formatMessage(label);

    const createHeader =
        (reportMeasure: ReportMeasure, isLeftAligned = true) =>
        ({ sortedState }: ReactTableTypes.HeaderContextType<any, unknown>): ReactNode => {
            const generalTooltip = commonMessages.tooltips[reportMeasure.measure];
            const reportSpecificTooltip =
                reportSpecificMessages.tooltips[reportCategory]?.[reportName]?.[reportMeasure.measure];
            return (
                <SortableTableHeader
                    alignment={isLeftAligned ? BaseStyleEnums.Alignments.left : BaseStyleEnums.Alignments.center}
                    sortDesc={sortedState?.desc}
                    sortIndex={sortedState?.index}
                >
                    <HeaderCell
                        tooltip={reportMeasure.tooltip || translateIfExist(reportSpecificTooltip || generalTooltip)}
                        label={reportMeasure.label}
                    />
                </SortableTableHeader>
            );
        };

    const columnId = reportMeasure.measure;
    const columnObj: Column = {
        cell: ({ row }) => stringFormatter.cellDisplay(statFormatter.displayValueAs(columnId)(row.original[columnId])),
        header: createHeader(reportMeasure, false),
        sortDescFirst: true,
        id: columnId,
        maxSize: Infinity,
        minSize: getMinColumnSize(columnId, reportMeasure.label),
        size: 50,
        meta: {
            style: {
                textAlign: 'center',
            },
        },
    };
    const excelColumns = [
        {
            accessorKey: columnId,
            header: reportMeasure.label,
        },
    ];

    // NOTE Some cases are left out, (gamesPlayed, etc.) because they don't need any changes to the default properties
    // NOTE Commented out lines are to quickly revert back to a previous iteration of this module. Will delete soon.
    switch (columnId) {
        // Columns To Hide
        case 'franchiseId':
        case 'gameId':
        case 'opponentTeamAbbrev':
        case 'playerId':
        case 'teamId':
            columnObj.meta = { hidden: true };
            break;

        // Non formatted cells
        case 'draftYear':
            columnObj.cell = ({ row }) =>
                row.original[columnId] !== null && row.original[columnId] !== undefined
                    ? row.original[columnId]
                    : intl.formatMessage(commonMessages.misc.noInfo);
            break;

        case 'teamAbbrev':
        case 'teamAbbrevs':
            columnObj.sortDescFirst = false;
            columnObj.maxSize = 80;
            columnObj.cell = ({ row }) => {
                const teamName = stringFormatter.cellDisplay(row.original[columnId]).replaceAll(',', ', ');
                return <div title={teamName}>{teamName}</div>;
            };
            columnObj.meta.excelColumns = excelColumns;
            break;
        case 'birthDate':
            columnObj.maxSize = 120;
            columnObj.sortDescFirst = false;
            break;

        case 'plusMinus':
            columnObj.cell = ({ row }) => domFormatter.getPosNegCell({
                value: row.original.plusMinus, posColor: theme.Text.primary, negColor: theme.Text.primary,
            });
            columnObj.meta.excelColumns = excelColumns;
            break;

        // 1 Decimal cells (percentage) xxx.x Special Case
        case 'savePct5v5':
        case 'shootingPlusSavePct5v5':
        case 'skaterSavePct5v5':
        case 'skaterShootingPlusSavePct5v5':
            columnObj.cell = ({ row }) =>
                statFormatter.decimalFormatter(1)(row.original[columnId] * 100);
            break;

        // 1 Decimal cells (non-percentages)
        case 'netShotsPerGame':
        case 'ppIndividualSatForPer60':
        case 'ppShotsPer60':
        case 'shiftsPerGame':
        case 'shIndividualSatForPer60':
        case 'shShotsPer60':
        case 'shotsAgainstPer60':
        case 'shotsAgainstPerGame':
        case 'shotsForPerGame':
            columnObj.cell = ({ row }) => statFormatter.decimalFormatter(1)(row.original[columnId], true);
            break;

        // 1 Decimal cells (percentages)
        case 'careerShootoutShootingPct':
        case 'completeGamePct':
        case 'evFaceoffPct':
        case 'evenStrengthGoalsForPct':
        case 'faceoffPct5v5':
        case 'faceoffWinPct':
        case 'goalsForPct':
        case 'goalsPct':
        case 'offensiveZoneStartPct5v5':
        case 'onIceShootingPct':
        case 'onIceShootingPct5v5':
        case 'overallPenaltyKillPct':
        case 'overallPowerPlayPct':
        case 'penaltyKillNetPct':
        case 'penaltyKillPct':
        case 'penaltyKillPct3v4':
        case 'penaltyKillPct3v5':
        case 'penaltyKillPct4v5':
        case 'penaltyMinutesPerTimeOnIce':
        case 'penaltyShotShootingPct':
        case 'powerPlayNetPct':
        case 'powerPlayPct':
        case 'powerPlayPct4v3':
        case 'powerPlayPct5v3':
        case 'powerPlayPct5v4':
        case 'ppFaceoffPct':
        case 'ppShootingPct':
        case 'satPct':
        case 'satPctAhead':
        case 'satPctBehind':
        case 'satPctClose':
        case 'satPctTied':
        case 'satPercentage':
        case 'satPercentageAhead':
        case 'satPercentageBehind':
        case 'satPercentageClose':
        case 'satPercentageTied':
        case 'satRelative':
        case 'satRelative5v5':
        case 'shFaceoffPct':
        case 'shTimeOnIcePctPerGame':
        case 'shootingPct':
        case 'shootingPct5v5':
        case 'shootingPctBackhand':
        case 'shootingPctBat':
        case 'shootingPctBetweenLegs':
        case 'shootingPctPoke':
        case 'shootingPctSlap':
        case 'shootingPctSnap':
        case 'shootingPctTipIn':
        case 'shootingPctWrapAround':
        case 'shootingPctWrist':
        case 'shootoutShootingPct':
        case 'shShootingPct':
        case 'usatPct':
        case 'usatPctAhead':
        case 'usatPctBehind':
        case 'usatPctClose':
        case 'usatPctTied':
        case 'usatPercentage':
        case 'usatPercentageAhead':
        case 'usatPercentageBehind':
        case 'usatPercentageTied':
        case 'usatPrecentageClose':
        case 'usatRelative':
        case 'zoneStartPct':
        case 'zoneStartPct5v5':
        case 'defensiveZoneFaceoffPct':
        case 'neutralZoneFaceoffPct':
        case 'offensiveZoneFaceoffPct':
        case 'offensiveZoneStartRatio':
        case 'defensiveZoneStartPct':
        case 'neutralZoneStartPct':
        case 'offensiveZoneStartPct':
        case 'ppTimeOnIcePctPerGame':
        case 'qualityStartsPct':
        case 'shootingPctCradle':
        case 'shootingPctDeflected':
            columnObj.cell = ({ row }) =>
                statFormatter.decimalFormatter(1)(row.original[columnId] * 100);
            break;

        // 2 Decimal cells (non-percentage)
        case 'assistsPer605v5':
        case 'blocksPerGame':
        case 'evGoalsAgainstPerGame':
        case 'evGoalsForPerGame':
        case 'evNetGoalsPerGame':
        case 'goalsAgainstPerGame':
        case 'goalsAgainstPerRegulationLoss':
        case 'goalsForAverage':
        case 'goalsForPerGame':
        case 'goalsPer605v5':
        case 'goalsPerGame':
        case 'assistsPerGame':
        case 'hitsPerGame':
        case 'individualSatForPer60':
        case 'individualShotsForPer60':
        case 'netMinorPenaltiesPer60':
        case 'penaltyMinutesPerGame':
        case 'pkNetGoalsPerGame':
        case 'pointsPer605v5':
        case 'ppGoalsAgainstPerGame':
        case 'ppGoalsPerGame':
        case 'ppNetGoalsPerGame':
        case 'ppOpportunitiesPerGame':
        case 'primaryAssistsPer605v5':
        case 'primaryAssistsPerGame':
        case 'satPerGiveaway':
        case 'secondaryAssistsPer605v5':
        case 'secondaryAssistsPerGame':
        case 'shGoalsAgainstPerGame':
        case 'shGoalsForPerGame':
        case 'shotsPerGame':
        case 'specialTeamsNetGoalsPerGame':
        case 'timesShorthandedPerGame':
        case 'blockedShotsPer60':
        case 'giveawaysPer60':
        case 'hitsPer60':
        case 'takeawaysPer60':
        case 'netGoalsPerGame':
        case 'pointsPerGame':
        case 'ppGoalsAgainstPer60':
        case 'ppGoalsForPer60':
        case 'ppGoalsPer60':
        case 'ppPointsPer60':
        case 'shGoalsPer60':
        case 'shPointsPer60':
        case 'netPenaltiesPer60':
        case 'penaltiesDrawnPer60':
        case 'penaltiesTakenPer60':
        case 'ppPrimaryAssistsPer60':
        case 'ppSecondaryAssistsPer60':
        case 'shPrimaryAssistsPer60':
        case 'shSecondaryAssistsPer60':
        case 'shotDifferentialPerGame':
            columnObj.cell = ({ row }) => statFormatter.decimalFormatter(2)(row.original[columnId], true);
            break;
        case 'goalsAgainstAverage':
            columnObj.cell = ({ row }) => statFormatter.decimalFormatter(2)(row.original[columnId], true);
            columnObj.sortDescFirst = false;
            break;

        // 3 Decimal Cells - no leading zero
        case 'careerSavePct':
        case 'careerShootoutSavePct':
        case 'evSavePct':
        case 'gamesRelievedSavePct':
        case 'gamesStartedSavePct':
        case 'penaltyShotSavePct':
        case 'pointPct':
        case 'pointsPct':
        case 'ppSavePct':
        case 'savePct':
        case 'savePctDaysRest0':
        case 'savePctDaysRest1':
        case 'savePctDaysRest2':
        case 'savePctDaysRest3':
        case 'savePctDaysRest4Plus':
        case 'shSavePct':
        case 'shootoutSavePct':
        case 'shootoutShootingPlusSavePct':
        case 'shootoutWinPct':
        case 'winPctLeadPeriod1':
        case 'winPctLeadPeriod2':
        case 'winPctOneGoalGames':
        case 'winPctScoringFirst':
        case 'winPctThreeGoalGames':
        case 'winPctTrailPeriod1':
        case 'winPctTrailPeriod2':
        case 'winPctTrailingFirst':
        case 'winPctTwoGoalGames':
            columnObj.cell = ({ row }) =>
                statFormatter.decimalFormatter(3)(row.original[columnId], true).replace(/^0+/, '');
            break;

        // Time Cells
        case 'evTimeOnIce':
        case 'evTimeOnIcePerGame':
        case 'evenStrengthTimeOnIcePerGame':
        case 'otTimeOnIce':
        case 'otTimeOnIcePerOtGame':
        case 'penaltySecondsPerGame':
        case 'pkTimeOnIcePerGame':
        case 'powerPlayTimeOnIcePerGame':
        case 'ppTimeOnIce':
        case 'ppTimeOnIcePerGame':
        case 'shTimeOnIce':
        case 'shTimeOnIcePerGame':
        case 'shortHandedTimeOnIcePerGame':
        case 'timeOnIce':
        case 'timeOnIce3v4':
        case 'timeOnIce3v5':
        case 'timeOnIce4v3':
        case 'timeOnIce4v5':
        case 'timeOnIce5v3':
        case 'timeOnIce5v4':
        case 'timeOnIcePerGame':
        case 'timeOnIcePerGame5v5':
        case 'timeOnIcePerShift':
        case 'timeOnIcePp':
        case 'timeOnIceShorthanded':
            columnObj.cell = ({ row }) =>
                row.original[columnId] !== null
                    ? dateTimeFormatter.displaySecondsFormatted(row.original[columnId])
                    : intl.formatMessage(commonMessages.misc.noInfo);
            break;

        // Custom cells
        case 'goalieFullName':
        case 'skaterFullName':
            columnObj.cell = ({ row }) => <PlayerNameCell row={row} />;
            columnObj.sortDescFirst = false;

            // Set the header to left align the label
            columnObj.header = createHeader(reportMeasure, true);
            columnObj.meta = {
                ...LEFT_ALIGNED_COLUMN,
                excelColumns,
                pinned: ReactTableEnums.Pinned.LEFT,
            };
            break;

        case 'franchiseName':
        case 'teamFullName':
            columnObj.sortDescFirst = false;
            columnObj.header = createHeader(reportMeasure, true);
            columnObj.maxSize = 200;
            columnObj.meta = {
                ...LEFT_ALIGNED_COLUMN,
                excelColumns,
                pinned: ReactTableEnums.Pinned.LEFT,
            };
            break;

        case 'firstSeasonForGameType':
        case 'seasonId':
            columnObj.cell = ({ row }) => hockeyFormatter.singleSeasonFormatter(row.original[columnId]);
            columnObj.header = createHeader(reportMeasure, true);
            columnObj.meta = {
                ...LEFT_ALIGNED_COLUMN,
                excelColumns,
            };
            break;

        case 'gameDate':
            columnObj.cell = ({ row }) => <GameCell intl={intl} gameData={row.original} />;
            columnObj.header = () => (
                <HeaderCell
                    tooltip={intl.formatMessage(commonMessages.tooltips.gameId)}
                    label={intl.formatMessage(commonMessages.tooltips.gameId)}
                />
            );
            columnObj.maxSize = 160;
            columnObj.meta = {
                ...LEFT_ALIGNED_COLUMN,
                excelColumns,
            };
            break;

        case 'height':
            columnObj.cell = ({ row }) => {
                const height = Number(row.original.height);
                if (height) {
                    const feet = Math.floor(height / 12);
                    const inches = height % 12;
                    return (
                        <>
                            <FormattedNumber value={feet} style="unit" unit="foot" unitDisplay="narrow" />
                            <FormattedNumber value={inches} style="unit" unit="inch" unitDisplay="narrow" />
                        </>
                    );
                }
                return <FormattedMessage {...commonMessages.misc.noInfo} />;
            };
            break;

        default:
            break;
    }

    // Ensure the size is at least the minimum size
    columnObj.size = Math.max(columnObj.minSize || 0, columnObj.size || 0);
    columnObj.meta.excelHeader = reportMeasure.label;
    return columnHelper.accessor(columnId, columnObj);
};

export default cellFormatter;
